import { Group, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import type { ScheduleForm, WallClock } from "./block-schedule";

export interface ScheduleFieldsProps {
  readonly value: ScheduleForm;
  /** The church's zone: the dates and times are read on its clock. */
  readonly timeZone: string;
  readonly disabled: boolean;
  readonly onChange: (next: ScheduleForm) => void;
}

/**
 * When visitors see a block (ADR 0031, amendment of 2026-10-03): always, the default, or between an optional show-from
 * and an optional show-until, each a date with an optional time on the church's clock.
 */
export function ScheduleFields({ value, timeZone, disabled, onChange }: ScheduleFieldsProps) {
  const set =
    (half: "from" | "until", part: keyof WallClock) =>
    (event: { currentTarget: { value: string } }) => {
      const next = event.currentTarget.value;
      onChange({ ...value, [half]: { ...value[half], [part]: next } });
    };
  return (
    <Stack gap="xs" component="section" aria-labelledby="block-schedule-heading">
      <Text component="h3" id="block-schedule-heading" fw={600} size="sm" m={0}>
        When visitors see it
      </Text>
      <SegmentedControl
        aria-label="When visitors see this block"
        disabled={disabled}
        value={value.mode}
        onChange={(mode) => {
          onChange({ ...value, mode: mode === "dates" ? "dates" : "always" });
        }}
        data={[
          { value: "always", label: "Always" },
          { value: "dates", label: "Between dates" },
        ]}
      />
      {value.mode === "always" ? (
        <Text size="sm" c="dimmed">
          The block shows whenever the page is published.
        </Text>
      ) : (
        <>
          <Text size="sm" c="dimmed">
            Dates and times are the church&apos;s ({timeZone}). Leave a time empty for the whole
            day, and a date empty for no start or no end. Once the page is published the block
            appears and disappears on its own; there is no need to publish again.
          </Text>
          <Group gap="sm" grow align="flex-start">
            <TextInput
              type="date"
              label="Show from"
              disabled={disabled}
              value={value.from.date}
              onChange={set("from", "date")}
            />
            <TextInput
              type="time"
              label="Show from time"
              disabled={disabled}
              value={value.from.time}
              onChange={set("from", "time")}
            />
          </Group>
          <Group gap="sm" grow align="flex-start">
            <TextInput
              type="date"
              label="Show until"
              description="The last day it shows"
              disabled={disabled}
              value={value.until.date}
              onChange={set("until", "date")}
            />
            <TextInput
              type="time"
              label="Show until time"
              description="It is hidden from this time"
              disabled={disabled}
              value={value.until.time}
              onChange={set("until", "time")}
            />
          </Group>
        </>
      )}
    </Stack>
  );
}
